import { isNil } from 'lodash';
import type {
  InheritedSettingsT,
  RateSettingTaxes,
  TaxClassificationSettings,
  RateSpecificSettings,
} from 'resources/rate_settings/shared/types/inheritedSettings.types';

export type RateClassificationT = 'ten99' | 'w2.local' | 'w2.travel';

export class RateSettingsDisplayHelper {
  constructor(private rateSetting: RateSettingTaxes | { settings: InheritedSettingsT }) {}

  private isRateSettingTaxes(
    setting: RateSettingTaxes | { settings: InheritedSettingsT },
  ): setting is RateSettingTaxes {
    return (
      'location' in setting ||
      ('settings' in setting && ('ten99' in setting.settings || 'w2' in setting.settings))
    );
  }

  getPlan() {
    if (this.isRateSettingTaxes(this.rateSetting) && this.rateSetting.location) {
      return this.rateSetting.location.primary_billing_group?.plan || 'standard';
    }
    return 'standard';
  }

  getNestedObject() {
    if (this.getPlan() === 'enterprise_w2') {
      return 'w2.local';
    }

    return 'ten99';
  }

  getBlendedRateMarkup(forceNestedObject?: string): number | null {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const settings = this.getSettingsForClassification(nestedObject);

    return !isNil(settings?.blended_rates?.blended_rate_markup)
      ? settings?.blended_rates?.blended_rate_markup
      : null;
  }

  getMultiplier(classification?: RateClassificationT) {
    return 1 + this.getBlendedRateMarkup(classification);
  }

  getSettingsForClassification(
    nestedObject: string,
    settingsSource?: TaxClassificationSettings | InheritedSettingsT,
  ): Partial<RateSpecificSettings> {
    const [taxClassification, subHash] = nestedObject?.split('.');
    const settings = settingsSource || this.rateSetting.settings;

    if (taxClassification === 'ten99' || taxClassification === 'w2') {
      const classification = settings[taxClassification as keyof typeof settings];
      if (
        subHash &&
        classification &&
        typeof classification === 'object' &&
        subHash in classification
      ) {
        return classification[
          subHash as keyof typeof classification
        ] as Partial<RateSpecificSettings>;
      }
      return classification as Partial<RateSpecificSettings>;
    }

    return {};
  }

  getActiveRate(forceNestedObject?: string): '8-hour-rate' | 'blended-rate' | '' {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const taxClassificationNestedObject = this.getSettingsForClassification(nestedObject);

    const useBlendedRate = taxClassificationNestedObject?.use_blended_rate;
    if (isNil(useBlendedRate)) {
      return null;
    }
    return useBlendedRate ? 'blended-rate' : '8-hour-rate';
  }

  getActiveRateObjKey(forceNestedObject?: RateClassificationT) {
    return this.getActiveRate(forceNestedObject) === '8-hour-rate'
      ? 'non_blended_rates'
      : 'blended_rates';
  }

  getActiveRateStructureLabel(forceNestedObject?: RateClassificationT): '8hr' | '12hr' | '' {
    const nestedObject = forceNestedObject || this.getNestedObject();
    const activeRateStructureLabel = this.getActiveRate(nestedObject);
    if (isNil(activeRateStructureLabel)) {
      return '';
    }
    return activeRateStructureLabel === '8-hour-rate' ? '8hr' : '12hr';
  }

  getActiveChargeRateCents(
    classification?: RateClassificationT,
    withMarkup?: boolean,
  ): number | undefined {
    const applicableClassification = classification || this.getNestedObject();
    const withMarkupSettings = this.isRateSettingTaxes(this.rateSetting)
      ? this.rateSetting.with_markup_settings
      : undefined;
    const applicableSettings = withMarkup
      ? withMarkupSettings || this.rateSetting.settings
      : this.rateSetting.settings;

    const settingsForClassification = this.getSettingsForClassification(
      applicableClassification,
      applicableSettings,
    );

    return settingsForClassification?.charge_rate_cents;
  }

  getChargeRateCents(classification?: RateClassificationT, forceNestedObject?: string): number {
    const activeRate = this.getActiveRate(forceNestedObject);
    const chargeRateCents = this.getActiveChargeRateCents(classification);

    if (activeRate === '8-hour-rate') {
      return chargeRateCents;
    }

    const multiplier = this.getMultiplier(classification);

    return chargeRateCents * multiplier;
  }

  get12hRate(classification: RateClassificationT) {
    const multiplier = this.getMultiplier(classification);
    const chargeRateCents = this.getActiveChargeRateCents(classification);

    return chargeRateCents * multiplier;
  }

  format12hMarkupDisplay(classification?: RateClassificationT) {
    const blendedRateMarkup = this.getBlendedRateMarkup(classification);
    return !isNil(blendedRateMarkup) ? `${(blendedRateMarkup * 100).toFixed(2)}%` : '';
  }
}
